/**
 * authFetch - Azure AD Bearer token injector
 *
 * Injects Authorization: Bearer <id_token> on every backend API call.
 * If called before the token is set (race condition on cold load), it waits
 * until setAuthToken() is called from App.tsx, then proceeds automatically.
 * Calls that already carry an Authorization header (MSA/Flux) are left untouched.
 */

const TOKEN_WAIT_TIMEOUT_MS = 15_000;
const REQUEST_TIMEOUT_MS = 60_000;

let _token: string | null = null;
let _resolve: ((t: string) => void) | null = null;
const _tokenReady: Promise<string> = new Promise(res => { _resolve = res; });

/**
 * Decode JWT token payload (without verification)
 */
const decodeJWT = (token: string): any => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
};

/**
 * Check if token is expired or about to expire (within 5 minutes)
 */
export const isTokenExpired = (token: string): boolean => {
  const payload = decodeJWT(token);
  if (!payload || !payload.exp) {
    console.warn('⚠️ Cannot decode token or missing exp claim');
    return true;
  }

  const expirationTime = payload.exp * 1000; // Convert to milliseconds
  const currentTime = Date.now();
  const bufferTime = 5 * 60 * 1000; // 5 minutes buffer

  const isExpired = currentTime >= (expirationTime - bufferTime);
  
  if (isExpired) {
    const timeLeft = Math.floor((expirationTime - currentTime) / 1000);
    console.warn(`⚠️ Token expired or expiring soon (${timeLeft}s left)`);
  }

  return isExpired;
};

/**
 * Get token expiration info for logging/debugging
 */
export const getTokenInfo = (token: string): { exp: number; iat: number; timeLeft: string } | null => {
  const payload = decodeJWT(token);
  if (!payload) return null;

  const exp = payload.exp * 1000;
  const iat = payload.iat * 1000;
  const now = Date.now();
  const timeLeftMs = exp - now;
  const timeLeftMin = Math.floor(timeLeftMs / 60000);
  const timeLeftSec = Math.floor((timeLeftMs % 60000) / 1000);

  return {
    exp,
    iat,
    timeLeft: timeLeftMs > 0 
      ? `${timeLeftMin}m ${timeLeftSec}s` 
      : 'EXPIRED'
  };
};

/**
 * Get the current authenticated user's identity from the stored JWT token.
 * Checks claims in priority order: preferred_username → upn → email → oid → sub
 */
export const getCurrentUser = (): string => {
  if (!_token) return 'unknown';
  const payload = decodeJWT(_token);
  if (!payload) return 'unknown';
  return (
    payload.preferred_username ||
    payload.upn ||
    payload.email ||
    payload.oid ||
    payload.sub ||
    'unknown'
  );
};

export const setAuthToken = (token: string): void => {
  _token = token;
  
  // Log token expiration info
  const tokenInfo = getTokenInfo(token);
  if (tokenInfo) {
    const issuedAt = new Date(tokenInfo.iat).toLocaleString();
    const expiresAt = new Date(tokenInfo.exp).toLocaleString();
    const tokenPreview = token.substring(0, 50) + '...';
    console.log('🔑 Token set:');
    console.log(`   Token preview: ${tokenPreview}`);
    console.log(`   Issued at: ${issuedAt}`);
    console.log(`   Expires at: ${expiresAt}`);
    console.log(`   Time remaining: ${tokenInfo.timeLeft}`);
  }
  
  if (_resolve) {
    _resolve(token);
    _resolve = null;
  }
};

export const authFetch = async (url: string, init: RequestInit = {}): Promise<Response> => {
  // If token not yet set, wait for it (resolves as soon as App.tsx calls setAuthToken)
  let token = _token;
  if (!token) {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<never>((_, reject) => {
      timer = setTimeout(
        () => reject(new Error('Authentication token not available. Please refresh the page or sign in again.')),
        TOKEN_WAIT_TIMEOUT_MS
      );
    });
    try {
      token = await Promise.race([_tokenReady, timeout]);
    } finally {
      clearTimeout(timer);
    }
  }

  // Check if token is expired or about to expire
  if (isTokenExpired(token)) {
    console.error('❌ Token is expired or about to expire! API call may fail.');
    console.log('💡 Tip: Refresh the page or re-login to get a new token');
  } else {
    const tokenInfo = getTokenInfo(token);
    if (tokenInfo) {
      console.log(`🌐 API call to: ${url.substring(0, 80)}...`);
      console.log(`   Using token with ${tokenInfo.timeLeft} remaining`);
    }
  }

  const headers = new Headers(init.headers as HeadersInit | undefined);
  if (!headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  // Bound the request so a hung backend surfaces as an error instead of an endless spinner
  const controller = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, REQUEST_TIMEOUT_MS);
  if (init.signal) {
    if (init.signal.aborted) controller.abort();
    else init.signal.addEventListener('abort', () => controller.abort(), { once: true });
  }
  const signal = controller.signal;

  try {
    return await fetch(url, { ...init, headers, signal });
  } catch (err) {
    if (timedOut) {
      throw new Error(`Request timed out after ${REQUEST_TIMEOUT_MS / 1000}s: ${url.split('?')[0]}`);
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
};

/**
 * authFetchWithCustomToken - Similar to authFetch but allows passing a custom token
 * Used for APIs that require different audience tokens (e.g., NFR API)
 */
export const authFetchWithCustomToken = async (
  url: string,
  customToken: string,
  init: RequestInit = {}
): Promise<Response> => {
  // Check if custom token is expired
  if (isTokenExpired(customToken)) {
    console.error('❌ Custom token is expired or about to expire!');
  } else {
    const tokenInfo = getTokenInfo(customToken);
    if (tokenInfo) {
      console.log(`🌐 API call with custom token to: ${url.substring(0, 80)}...`);
      console.log(`   Using token with ${tokenInfo.timeLeft} remaining`);
    }
  }

  const headers = new Headers(init.headers as HeadersInit | undefined);
  headers.set('Authorization', `Bearer ${customToken}`);

  return fetch(url, { ...init, headers });
};
